import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useAuth } from '../lib/auth';
import { getSubdomain } from '../lib/hostname';
import { getRootUrl } from '../lib/urls';
import { COPY_MAP } from '../lib/settings';
import { ICON_KEYS, PillarIcon } from '../lib/icons';

export const Route = createFileRoute('/admin')({
  component: AdminPage,
});

/** Parse date strings from the DB — handles SQLite, Postgres, and ISO formats.
 *  Postgres `now()::text` gives `2026-04-17 15:10:29.931116+00` — note the short `+00` offset. */
function parseDate(raw: string | null | undefined): Date {
  if (!raw) return new Date(NaN);
  let s = raw.trim();
  // Replace space between date and time with T
  s = s.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:)/, '$1T$2');
  // Fix short timezone offset: +00 → +00:00 (JS Date requires the colon)
  s = s.replace(/([+-]\d{2})$/, '$1:00');
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d;
  // Fallback: treat as UTC if no timezone
  return new Date(s + 'Z');
}

// ─── Types ──────────────────────────────────────────────────────────────────────

interface AdminOrderItem {
  id: string;
  quote_id: string;
  qty: number;
  notes: string | null;
  unit_price_sar: number;
  line_total_sar: number;
  type: string | null;
  style: string | null;
  image_url: string | null;
}

interface AdminOrder {
  id: string;
  name: string;
  phone: string;
  city: string | null;
  note: string | null;
  estimated_total_sar: number;
  status: string;
  assigned_to: string | null;
  created_at: string;
  items: AdminOrderItem[];
}

interface AdminQuote {
  id: string;
  analysis_id: string;
  custom_price_sar: number;
  market_min_sar: number;
  market_max_sar: number;
  created_at: string;
  type: string | null;
  style: string | null;
  image_url: string | null;
  order_count: number;
}

type AppSettings = Record<string, unknown>;

// ─── Auth gate ──────────────────────────────────────────────────────────────────

function AdminPage() {
  const { user, isLoading, login, logout, tenant } = useAuth();
  const isDemo = getSubdomain() === 'demo';
  const navigate = useNavigate();

  // Check if onboarding is needed (new tenant, settings not configured yet)
  const { data: settings } = useQuery({
    queryKey: ['admin-settings-check'],
    queryFn: async () => {
      const res = await fetch('/api/admin/settings', { credentials: 'include' });
      if (!res.ok) return null;
      return (await res.json()) as { settings: Record<string, unknown> };
    },
    enabled: !!user && !isDemo,
  });

  if (isLoading && !isDemo) return <Page><p className="text-oud-600 text-base text-center pt-20">…</p></Page>;

  // Demo tenant: skip login, show dashboard directly (read-only)
  if (isDemo) return <Dashboard onLogout={() => { window.location.href = '/'; }} isDemo />;

  if (!user) return <LoginGate onLogin={login} />;

  // Redirect to onboarding if not completed
  if (settings && !settings.settings['onboarding.completed']) {
    window.location.href = '/admin/onboarding';
    return null;
  }

  return <Dashboard onLogout={logout} />;
}

function Page({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={`min-h-screen bg-sand text-oud-900 ${className ?? ''}`} dir="rtl">{children}</div>;
}

function LoginGate({ onLogin }: { onLogin: (email: string, password: string) => Promise<{ tenantSlug: string }> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const isWelcome = new URLSearchParams(window.location.search).has('welcome');

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try {
      await onLogin(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل تسجيل الدخول');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page className="flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-oud-900 text-xl font-black">تسجيل الدخول</h1>
          <a href="/" className="text-sm text-oud-500 hover:text-oud-900 font-bold flex items-center gap-1">
            <span>رجوع</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden><path d="M15 6l-6 6 6 6" /></svg>
          </a>
        </div>
        {isWelcome ? (
          <div className="bg-gradient-to-br from-gold/20 to-gold/10 border border-gold/30 rounded-3xl p-5 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold to-oud-700 flex items-center justify-center mx-auto shadow-soft">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fbf7f2" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden>
                <path d="M5 12l5 5L20 7" />
              </svg>
            </div>
            <div>
              <p className="text-lg font-black text-oud-900">صفحتك جاهزة!</p>
              <p className="text-sm text-oud-600 mt-1">سجّل دخولك لإكمال الإعداد واستقبال أول طلباتك</p>
            </div>
          </div>
        ) : null}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="البريد الإلكتروني" autoFocus
          className="w-full bg-cream border border-oud-100 rounded-2xl px-4 py-3 text-oud-900 placeholder-oud-300 text-base focus:outline-none focus:border-gold" />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="كلمة المرور"
          className="w-full bg-cream border border-oud-100 rounded-2xl px-4 py-3 text-oud-900 placeholder-oud-300 text-base focus:outline-none focus:border-gold" />
        {error ? <p className="text-sm text-red-700">{error}</p> : null}
        <button type="submit" disabled={busy || !email.trim() || !password}
          className="w-full bg-oud-800 text-sand rounded-2xl px-4 py-3 text-base font-bold disabled:opacity-40 hover:bg-oud-900 transition-colors">
          {busy ? 'جارٍ الدخول…' : 'دخول'}
        </button>
      </form>
    </Page>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────────

type Tab = 'orders' | 'quotes' | 'settings';

function Dashboard({ onLogout, isDemo }: { onLogout: () => void; isDemo?: boolean }) {
  const [tab, setTab] = useState<Tab>('orders');

  const tenantQ = useQuery({
    queryKey: ['admin-tenant-info'],
    queryFn: async () => {
      const res = await fetch('/api/tenant', { credentials: 'include' });
      if (!res.ok) return null;
      return (await res.json()) as { id: string; slug: string; businessName: string; status: string; trialEndsAt: string | null; paidUntil: string | null };
    },
    refetchInterval: 60_000,
  });
  const tenantInfo = tenantQ.data;

  const ordersQ = useQuery({
    queryKey: ['admin-orders'],
    queryFn: () => adminFetch<{ orders: AdminOrder[] }>('/api/admin/orders'),
    refetchInterval: 3000, refetchIntervalInBackground: true,
  });
  const quotesQ = useQuery({
    queryKey: ['admin-quotes'],
    queryFn: () => adminFetch<{ quotes: AdminQuote[] }>('/api/admin/quotes'),
    refetchInterval: 3000, refetchIntervalInBackground: true,
  });
  const settingsQ = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => adminFetch<{ settings: AppSettings }>('/api/admin/settings'),
  });

  const orders = ordersQ.data?.orders ?? [];
  const quotes = quotesQ.data?.quotes ?? [];

  return (
    <Page>
      {isDemo ? (
        <div className="bg-gold/15 border-b border-gold/30 px-5 py-2.5 text-center">
          <p className="text-sm text-gold-deep font-bold">
            هذا عرض تجريبي · الإعدادات للعرض فقط ·{' '}
            <a href={`${getRootUrl()}/signup`} className="underline underline-offset-2 hover:text-oud-900">أنشئ متجرك الآن</a>
          </p>
        </div>
      ) : null}
      {!isDemo && tenantInfo ? <PlanBanner tenant={tenantInfo} /> : null}
      <header className="sticky top-0 z-10 bg-sand/90 backdrop-blur border-b border-oud-100">
        <div className="max-w-3xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1 bg-cream rounded-2xl p-1 border border-oud-100">
            <TabBtn active={tab === 'orders'} onClick={() => setTab('orders')}>
              الطلبات <span className="text-oud-400 mr-1">{orders.length}</span>
            </TabBtn>
            <TabBtn active={tab === 'quotes'} onClick={() => setTab('quotes')}>
              التسعيرات <span className="text-oud-400 mr-1">{quotes.length}</span>
            </TabBtn>
            <TabBtn active={tab === 'settings'} onClick={() => setTab('settings')}>
              الإعدادات
            </TabBtn>
          </div>
          <button type="button" onClick={onLogout} className="text-sm text-oud-500 hover:text-oud-900 font-bold">
            خروج
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5">
        {tab === 'orders' ? (
          <OrdersTab orders={orders} loading={ordersQ.isLoading} error={ordersQ.error} />
        ) : tab === 'quotes' ? (
          <QuotesTab quotes={quotes} loading={quotesQ.isLoading} error={quotesQ.error} />
        ) : (
          <SettingsTab settings={settingsQ.data?.settings} loading={settingsQ.isLoading} isDemo={isDemo} />
        )}
      </main>
    </Page>
  );
}

async function adminFetch<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json() as Promise<T>;
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={`text-sm font-bold rounded-xl px-4 py-2 transition-colors ${active ? 'bg-oud-800 text-sand shadow-soft' : 'text-oud-600 hover:text-oud-900'}`}>
      {children}
    </button>
  );
}

// ─── Orders Tab ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

type StatusFilter = 'all' | 'new' | 'handling' | 'done' | 'cancelled';
type AssignFilter = 'all' | 'unassigned' | string;

function OrdersTab({ orders, loading, error }: { orders: AdminOrder[]; loading: boolean; error: unknown }) {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [assignFilter, setAssignFilter] = useState<AssignFilter>('all');

  if (loading) return <Placeholder>جارٍ التحميل…</Placeholder>;
  if (error) return <Placeholder>خطأ في تحميل الطلبات</Placeholder>;
  if (orders.length === 0) return <Placeholder>لا توجد طلبات</Placeholder>;

  // Stats
  const stats = {
    total: orders.length,
    new: orders.filter((o) => o.status === 'new').length,
    handling: orders.filter((o) => o.status === 'handling').length,
    done: orders.filter((o) => o.status === 'done').length,
    cancelled: orders.filter((o) => o.status === 'cancelled').length,
    unassigned: orders.filter((o) => !o.assigned_to).length,
  };

  // Unique assignees
  const assignees = Array.from(
    new Set(orders.map((o) => o.assigned_to).filter((a): a is string => Boolean(a))),
  ).sort();

  // Filter
  let filtered = orders;
  if (statusFilter !== 'all') filtered = filtered.filter((o) => o.status === statusFilter);
  if (assignFilter === 'unassigned') filtered = filtered.filter((o) => !o.assigned_to);
  else if (assignFilter !== 'all') filtered = filtered.filter((o) => o.assigned_to === assignFilter);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(0, totalPages - 1));
  const paged = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        <StatCard label="الكل" value={stats.total} active={statusFilter === 'all'} onClick={() => { setStatusFilter('all'); setPage(0); }} />
        <StatCard label="جديد" value={stats.new} color="text-blue-700 bg-blue-50 border-blue-200" active={statusFilter === 'new'} onClick={() => { setStatusFilter('new'); setPage(0); }} />
        <StatCard label="قيد المعالجة" value={stats.handling} color="text-amber-700 bg-amber-50 border-amber-200" active={statusFilter === 'handling'} onClick={() => { setStatusFilter('handling'); setPage(0); }} />
        <StatCard label="مكتمل" value={stats.done} color="text-green-700 bg-green-50 border-green-200" active={statusFilter === 'done'} onClick={() => { setStatusFilter('done'); setPage(0); }} />
        <StatCard label="ملغي" value={stats.cancelled} color="text-red-700 bg-red-50 border-red-200" active={statusFilter === 'cancelled'} onClick={() => { setStatusFilter('cancelled'); setPage(0); }} />
      </div>

      {/* Assignee filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-oud-600 font-bold">المسؤول:</span>
        <button
          type="button"
          onClick={() => { setAssignFilter('all'); setPage(0); }}
          className={`text-xs font-bold rounded-xl px-3 py-1.5 border transition-colors ${assignFilter === 'all' ? 'bg-oud-800 text-sand border-oud-800' : 'bg-cream text-oud-600 border-oud-100 hover:border-oud-200'}`}
        >
          الكل
        </button>
        <button
          type="button"
          onClick={() => { setAssignFilter((f) => f === 'unassigned' ? 'all' : 'unassigned'); setPage(0); }}
          className={`text-xs font-bold rounded-xl px-3 py-1.5 border transition-colors ${assignFilter === 'unassigned' ? 'bg-oud-800 text-sand border-oud-800' : 'bg-cream text-oud-600 border-oud-100 hover:border-oud-200'}`}
        >
          بدون تعيين ({stats.unassigned})
        </button>
        {assignees.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => { setAssignFilter((f) => f === name ? 'all' : name); setPage(0); }}
            className={`text-xs font-bold rounded-xl px-3 py-1.5 border transition-colors ${assignFilter === name ? 'bg-oud-800 text-sand border-oud-800' : 'bg-cream text-oud-600 border-oud-100 hover:border-oud-200'}`}
          >
            {name}
          </button>
        ))}
      </div>

      {/* Results count */}
      {filtered.length !== orders.length ? (
        <p className="text-sm text-oud-500">{filtered.length} نتيجة</p>
      ) : null}

      {/* Order cards */}
      {paged.length === 0 ? (
        <Placeholder>لا توجد طلبات تطابق الفلتر</Placeholder>
      ) : (
        paged.map((o) => <OrderCard key={o.id} order={o} />)
      )}

      {totalPages > 1 ? <Pager page={safePage} totalPages={totalPages} onChange={setPage} /> : null}
    </div>
  );
}

function StatCard({ label, value, color, active, onClick }: {
  label: string;
  value: number;
  color?: string;
  active?: boolean;
  onClick: () => void;
}) {
  const base = color ?? 'text-oud-700 bg-cream border-oud-100';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border p-3 text-center transition-all ${base} ${active ? 'ring-2 ring-gold shadow-soft scale-[1.02]' : 'hover:shadow-soft'}`}
    >
      <div className="text-2xl font-black tabular-nums">{value}</div>
      <div className="text-[11px] font-bold mt-0.5">{label}</div>
    </button>
  );
}

const STATUS_LABELS: Record<string, string> = {
  new: 'جديد',
  handling: 'قيد المعالجة',
  done: 'مكتمل',
  cancelled: 'ملغي',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-50 text-blue-800 border-blue-200',
  handling: 'bg-amber-50 text-amber-800 border-amber-200',
  done: 'bg-green-50 text-green-800 border-green-200',
  cancelled: 'bg-red-50 text-red-800 border-red-200',
};

const ALL_STATUSES = ['new', 'handling', 'done', 'cancelled'] as const;

function OrderCard({ order }: { order: AdminOrder }) {
  const qc = useQueryClient();
  const [assigning, setAssigning] = useState(false);
  const [assignName, setAssignName] = useState(order.assigned_to ?? '');

  const statusMut = useMutation({
    mutationFn: async (body: { status: string; assigned_to?: string | null }) => {
      const res = await fetch(`/api/admin/orders/${order.id}/status`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`${res.status}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-orders'] }),
  });

  const created = parseDate(order.created_at);
  const statusColor = STATUS_COLORS[order.status] ?? STATUS_COLORS.new!;

  return (
    <div className="bg-cream border border-oud-100 rounded-3xl overflow-hidden shadow-soft">
      <div className="px-5 py-4 border-b border-oud-100/60">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-black text-lg text-oud-900">{order.name}</span>
              <a href={`tel:${normalizePhone(order.phone)}`} className="text-sm text-gold-deep hover:text-oud-900 font-mono flex items-center gap-1" dir="ltr">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 flex-none" aria-hidden>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.68 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.74.32 1.53.55 2.34.68A2 2 0 0 1 22 16.92Z" />
                </svg>
                {normalizePhone(order.phone)}
              </a>
            </div>
            <div className="text-sm text-oud-500 mt-0.5">
              #{order.id} · {order.city ?? '—'} · {created.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
            </div>
            {order.note ? (
              <p className="text-sm text-oud-700 mt-2 bg-sand border border-oud-100 rounded-xl px-3 py-2 italic">{order.note}</p>
            ) : null}
          </div>
          <div className="flex-none text-left">
            <div className="text-2xl font-black text-oud-900 tabular-nums">{order.estimated_total_sar.toLocaleString()}</div>
            <div className="text-xs text-oud-500">ر.س</div>
          </div>
        </div>

        {/* Status + assign */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-oud-600 font-bold">الحالة:</span>
            <div className="relative">
              <select
                value={order.status}
                onChange={(e) => statusMut.mutate({ status: e.target.value })}
                className={`min-w-[9.5rem] h-11 appearance-none rounded-xl border pr-3 pl-9 text-base font-bold cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold/50 ${statusColor}`}
              >
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-current/70"
                aria-hidden
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>
          </div>

          {order.assigned_to ? (
            <span className="text-sm text-oud-600 bg-sand border border-oud-100 rounded-xl px-3 py-1.5 flex items-center gap-1.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden>
                <circle cx="12" cy="8" r="4" />
                <path d="M20 21a8 8 0 1 0-16 0" />
              </svg>
              {order.assigned_to}
            </span>
          ) : null}

          <div className="flex-1" />

          {assigning ? (
            <form className="flex items-center gap-1.5" onSubmit={(e) => {
              e.preventDefault();
              statusMut.mutate({ status: order.status, assigned_to: assignName.trim() || null });
              setAssigning(false);
            }}>
              <input value={assignName} onChange={(e) => setAssignName(e.target.value)} placeholder="الاسم"
                className="bg-sand border border-oud-200 rounded-xl px-3 py-1.5 text-sm text-oud-900 w-28 focus:outline-none focus:border-gold" autoFocus />
              <button type="submit" className="text-sm text-oud-600 hover:text-oud-900 font-bold">✓</button>
              <button type="button" onClick={() => setAssigning(false)} className="text-sm text-oud-400">✕</button>
            </form>
          ) : (
            <button type="button" onClick={() => setAssigning(true)}
              className="text-sm text-oud-500 hover:text-oud-900 font-bold">
              {order.assigned_to ? 'إعادة تعيين' : 'تعيين'}
            </button>
          )}
        </div>
      </div>

      <ul className="divide-y divide-oud-100/60">
        {order.items.map((item) => (
          <li key={item.id} className="px-5 py-3 flex gap-3">
            {item.image_url ? (
              <a href={item.image_url} target="_blank" rel="noreferrer noopener" className="flex-none">
                <img src={item.image_url} alt="" className="w-16 h-16 object-cover rounded-xl bg-sand border border-oud-100" />
              </a>
            ) : (
              <div className="flex-none w-16 h-16 rounded-xl bg-sand border border-oud-100" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-base text-oud-900 truncate font-bold">
                  {item.type ?? '—'}
                  {item.style ? <span className="font-normal text-oud-600"> · {item.style}</span> : null}
                </p>
                <span className="text-base text-oud-900 tabular-nums font-bold">{item.line_total_sar.toLocaleString()}</span>
              </div>
              <p className="text-sm text-oud-500 mt-0.5">الكمية: {item.qty} × {item.unit_price_sar.toLocaleString()} ر.س</p>
              {item.notes ? <p className="text-sm text-oud-600 mt-1 italic">{item.notes}</p> : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Quotes Tab ─────────────────────────────────────────────────────────────────

function QuotesTab({ quotes, loading, error }: { quotes: AdminQuote[]; loading: boolean; error: unknown }) {
  const [page, setPage] = useState(0);
  if (loading) return <Placeholder>جارٍ التحميل…</Placeholder>;
  if (error) return <Placeholder>خطأ في تحميل التسعيرات</Placeholder>;
  if (quotes.length === 0) return <Placeholder>لا توجد تسعيرات</Placeholder>;
  const ordered = quotes.filter((q) => q.order_count > 0).length;
  const totalPages = Math.ceil(quotes.length / PAGE_SIZE);
  const paged = quotes.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  return (
    <>
      <p className="text-sm text-oud-600 mb-4">{ordered} من {quotes.length} تسعيرة تحولت إلى طلب</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {paged.map((q) => <QuoteCard key={q.id} quote={q} />)}
      </div>
      {totalPages > 1 ? <Pager page={page} totalPages={totalPages} onChange={setPage} /> : null}
    </>
  );
}

function QuoteCard({ quote }: { quote: AdminQuote }) {
  const created = parseDate(quote.created_at);
  const ordered = quote.order_count > 0;
  return (
    <a href={`/quote/${quote.id}`} target="_blank" rel="noreferrer noopener"
      className="bg-cream border border-oud-100 rounded-2xl overflow-hidden flex gap-3 hover:border-oud-200 transition-colors shadow-soft">
      {quote.image_url ? (
        <img src={quote.image_url} alt="" className="flex-none w-20 self-stretch object-cover bg-sand" />
      ) : (
        <div className="flex-none w-20 self-stretch bg-sand" />
      )}
      <div className="flex-1 min-w-0 py-2.5 pl-3">
        <div className="flex items-center gap-2">
          {ordered ? (
            <span className="text-[10px] font-bold bg-green-50 text-green-800 border border-green-200 rounded-full px-2 py-0.5">
              تم الطلب{quote.order_count > 1 ? ` ×${quote.order_count}` : ''}
            </span>
          ) : (
            <span className="text-[10px] font-bold bg-sand text-oud-500 border border-oud-100 rounded-full px-2 py-0.5">لم يتم الطلب</span>
          )}
        </div>
        <p className="text-sm text-oud-900 truncate mt-1 font-bold">
          {quote.type ?? '—'}
          {quote.style ? <span className="font-normal text-oud-600"> · {quote.style}</span> : null}
        </p>
        <p className="text-xs text-oud-500 mt-0.5 tabular-nums">
          {quote.custom_price_sar.toLocaleString()} ر.س · السوق {quote.market_min_sar.toLocaleString()}–{quote.market_max_sar.toLocaleString()}
        </p>
        <p className="text-[10px] text-oud-400 mt-1 font-mono">{quote.id} · {created.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}</p>
      </div>
    </a>
  );
}

// ─── Settings Tab ───────────────────────────────────────────────────────────────

const SETTING_GROUPS = [
  {
    title: 'تحليل الصور',
    description: '',
    fields: [
      {
        key: 'analysis.blockedFurnitureTypes',
        label: 'الأنواع غير المسموح بها',
        type: 'blockedTypes' as const,
        hint: 'افتراضيًا: لا يوجد منع. فعّل فقط الأنواع التي تريد حظرها.',
      },
    ],
  },
  {
    title: 'التسعير',
    description: '',
    fields: [
      { key: 'pricing.midpointFloorPct', label: 'الحد الأقصى للنسبة أقل من المتوسط', type: 'slider' as const, hint: 'السعر لا ينزل أكثر من هذه النسبة تحت متوسط السوق' },
      { key: 'pricing.vatRate', label: 'نسبة الضريبة (٪)', type: 'number' as const, hint: '' },
      { key: 'pricing.floorSAR', label: 'أقل سعر ممكن (ر.س)', type: 'number' as const, hint: 'لن يُعرض سعر أقل من هذا حتى إذا كان السوق أرخص' },
      { key: 'pricing.minQuantity', label: 'الحد الأدنى للقطع في الطلب', type: 'number' as const, hint: 'العدد التراكمي لجميع القطع. القيمة ١ تعني لا حد أدنى' },
      { key: 'pricing.minOrderTotalSAR', label: 'الحد الأدنى لإجمالي الطلب (ر.س)', type: 'number' as const, hint: 'شامل الضريبة. القيمة ٠ تعني لا حد أدنى' },
    ],
  },
  {
    title: 'العلامة التجارية',
    description: '',
    fields: [
      { key: 'brand.name', label: 'اسم العلامة (عربي)', type: 'text' as const, hint: '' },
      { key: 'brand.nameEn', label: 'اسم العلامة (إنجليزي)', type: 'text' as const, hint: '' },
      { key: 'brand.tagline', label: 'الشعار', type: 'text' as const, hint: '' },
      { key: 'brand.pageTitle', label: 'عنوان الصفحة', type: 'text' as const, hint: 'يظهر في تبويب المتصفح' },
      { key: 'brand.logoUrl', label: 'شعار العلامة', type: 'logo' as const, hint: 'PNG أو SVG · الحد الأقصى 2 ميغابايت' },
    ],
  },
  {
    title: 'التواصل',
    description: '',
    fields: [
      { key: 'contact.whatsappNumber', label: 'رقم واتساب (مع رمز الدولة)', type: 'text' as const, hint: 'مثال: 966501234567' },
      { key: 'contact.whatsappMessage', label: 'رسالة واتساب', type: 'text' as const, hint: 'استخدم {{orderLink}} لرابط الطلب أو {{orderId}} لرقم الطلب' },
    ],
  },
  {
    title: 'التوصيل',
    description: '',
    fields: [
      { key: 'delivery.label', label: 'وصف التوصيل', type: 'text' as const, hint: 'مثال: توصيل مجاني' },
      { key: 'delivery.fee', label: 'رسوم التوصيل (ر.س)', type: 'number' as const, hint: 'القيمة ٠ تعني أن التوصيل مجاني' },
    ],
  },
  {
    title: 'روابط خارجية',
    description: '',
    fields: [
      { key: 'links.websiteUrl', label: 'رابط الموقع / المتجر', type: 'text' as const, hint: 'رابط خارجي يظهر في الصفحة الرئيسية. اتركه فارغاً لإخفائه' },
      { key: 'links.websiteLabel', label: 'نص الزر', type: 'text' as const, hint: 'مثال: تصفّح مجموعاتنا' },
      { key: 'links.showWhatsappOnHome', label: 'إظهار واتساب في الصفحة الرئيسية', type: 'toggle' as const, hint: '' },
    ],
  },
  {
    title: 'تصميم الصفحة الرئيسية',
    description: '',
    fields: [
      { key: 'theme.homepage', label: 'النمط', type: 'theme' as const, hint: '' },
    ],
  },
  {
    title: 'فيديو الصفحة الرئيسية',
    description: 'فيديو قصير يعمل تلقائيًا في خلفية أعلى الصفحة. يُفضّل أن يكون الفيديو قصيرًا (أقل من ٣٠ ثانية) ومضغوطًا (أقل من ١٠ ميغابايت) لضمان سرعة التحميل.',
    fields: [
      { key: 'hero.videoEnabled', label: 'تفعيل فيديو الخلفية', type: 'toggle' as const, hint: '' },
      { key: 'hero.videoUrl', label: 'رابط الفيديو (MP4)', type: 'text' as const, hint: 'رابط مباشر لملف MP4 — يُفضّل رفعه على CDN أو استضافة سريعة' },
    ],
  },
  {
    title: 'الألوان',
    description: '',
    fields: [
      { key: 'theme.primary', label: 'اللون الرئيسي', type: 'color' as const, hint: 'أزرار، الهيدر، العناصر البارزة' },
      { key: 'theme.accent', label: 'لون التمييز', type: 'color' as const, hint: 'الشارات، الأيقونات، التفاصيل الذهبية' },
      { key: 'theme.background', label: 'لون الخلفية', type: 'color' as const, hint: 'خلفية الصفحة الأساسية' },
      { key: 'theme.card', label: 'لون البطاقات', type: 'color' as const, hint: 'خلفية الكروت والأقسام' },
      { key: 'theme.savings', label: 'لون التوفير', type: 'color' as const, hint: 'شارة نسبة التوفير' },
      { key: 'theme.text', label: 'لون النص', type: 'color' as const, hint: 'النصوص الأساسية' },
    ],
  },
  {
    title: 'النصوص',
    description: 'اتركها فارغة لاستخدام النصوص الافتراضية',
    fields: [
      { key: 'copy.heroHeadline', label: 'عنوان الصفحة الرئيسية', type: 'text' as const, hint: '' },
      { key: 'copy.heroSubheadline', label: 'وصف الصفحة الرئيسية', type: 'text' as const, hint: '' },
      { key: 'copy.instantBadge', label: 'شارة السعر الفوري', type: 'text' as const, hint: '' },
      { key: 'copy.pillar1', label: 'ميزة ١', type: 'pillar' as const, iconKey: 'pillars.icon1', hint: '' },
      { key: 'copy.pillar2', label: 'ميزة ٢', type: 'pillar' as const, iconKey: 'pillars.icon2', hint: '' },
      { key: 'copy.pillar3', label: 'ميزة ٣', type: 'pillar' as const, iconKey: 'pillars.icon3', hint: '' },
      { key: 'copy.pillar4', label: 'ميزة ٤', type: 'pillar' as const, iconKey: 'pillars.icon4', hint: '' },
      { key: 'copy.yourPrice', label: 'عنوان السعر', type: 'text' as const, hint: '' },
      { key: 'copy.marketPrice', label: 'عنوان متوسط السوق', type: 'text' as const, hint: '' },
      { key: 'copy.savingsLabel', label: 'عنوان التوفير', type: 'text' as const, hint: '' },
      { key: 'copy.addToOrder', label: 'زر الإضافة للطلب', type: 'text' as const, hint: '' },
      { key: 'copy.submitOrder', label: 'زر إرسال الطلب', type: 'text' as const, hint: '' },
      { key: 'copy.noPaymentTitle', label: 'عنوان "لا دفع الآن"', type: 'text' as const, hint: '' },
      { key: 'copy.noPaymentBody', label: 'وصف "لا دفع الآن"', type: 'text' as const, hint: '' },
      { key: 'copy.loadingTitle', label: 'عنوان شاشة التحميل', type: 'text' as const, hint: '' },
      { key: 'copy.whatsappButton', label: 'زر واتساب', type: 'text' as const, hint: '' },
    ],
  },
];

const BLOCKED_FURNITURE_TYPES = [
  { value: 'desk', label: 'مكاتب' },
  { value: 'dining_table', label: 'طاولات طعام' },
  { value: 'tv_stand', label: 'طاولات/وحدات تلفزيون' },
  { value: 'wardrobe', label: 'خزائن / دواليب' },
  { value: 'bed', label: 'أسرة' },
  { value: 'bookcase', label: 'مكتبات / رفوف' },
  { value: 'outdoor', label: 'أثاث خارجي' },
] as const;

function SettingsTab({ settings, loading, isDemo }: { settings?: AppSettings; loading: boolean; isDemo?: boolean }) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [draft, setDraft] = useState<AppSettings | null>(null);
  const [saved, setSaved] = useState(false);
  const hasChanges = draft && settings ? JSON.stringify(draft) !== JSON.stringify(settings) : false;

  useEffect(() => {
    if (settings && !draft) setDraft({ ...settings });
  }, [settings, draft]);

  // Live preview: inject draft colors as CSS vars while editing.
  // On unmount (leaving settings tab), restore saved values.
  useEffect(() => {
    if (!draft) return;
    const root = document.documentElement;
    const pairs: Array<[string, string]> = [
      ['--c-primary', String(draft['theme.primary'] ?? '')],
      ['--c-accent', String(draft['theme.accent'] ?? '')],
      ['--c-bg', String(draft['theme.background'] ?? '')],
      ['--c-card', String(draft['theme.card'] ?? '')],
      ['--c-savings', String(draft['theme.savings'] ?? '')],
      ['--c-text', String(draft['theme.text'] ?? '')],
    ];
    for (const [varName, val] of pairs) {
      if (val) root.style.setProperty(varName, val);
    }
    return () => {
      if (!settings) return;
      const saved: Array<[string, string]> = [
        ['--c-primary', String(settings['theme.primary'] ?? '')],
        ['--c-accent', String(settings['theme.accent'] ?? '')],
        ['--c-bg', String(settings['theme.background'] ?? '')],
        ['--c-card', String(settings['theme.card'] ?? '')],
        ['--c-savings', String(settings['theme.savings'] ?? '')],
        ['--c-text', String(settings['theme.text'] ?? '')],
      ];
      for (const [varName, val] of saved) {
        if (val) root.style.setProperty(varName, val);
      }
    };
  }, [draft, settings]);

  const saveMut = useMutation({
    mutationFn: async (updates: AppSettings) => {
      const res = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(`${res.status}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-settings'] });
      qc.invalidateQueries({ queryKey: ['app-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (loading || !draft) return <Placeholder>جارٍ التحميل…</Placeholder>;

  function updateField(key: string, value: unknown) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  // Live pricing example
  const midpointFloor = Number(draft['pricing.midpointFloorPct']) || 35;
  const vatRate = Number(draft['pricing.vatRate']) || 15;
  const floorSAR = Number(draft['pricing.floorSAR']) || 500;
  const exampleMin = 1500;
  const exampleMax = 4500;
  const exampleMid = (exampleMin + exampleMax) / 2;
  const preVat = 1 / (1 + vatRate / 100);
  const anchored = exampleMin * preVat;
  const midFloor = exampleMid * ((100 - midpointFloor) / 100) * preVat;
  const exampleRaw = Math.max(anchored, midFloor, floorSAR);
  const examplePrice = Math.round(exampleRaw / 50) * 50;
  const exampleDisplay = Math.round(examplePrice * (1 + vatRate / 100));

  return (
    <div className="space-y-8">
      {SETTING_GROUPS.map((group) => (
        <div key={group.title} className="bg-cream border border-oud-100 rounded-3xl p-5 shadow-soft">
          <h2 className="text-lg font-black text-oud-900 mb-1">{group.title}</h2>
          {group.description ? <p className="text-sm text-oud-500 mb-4">{group.description}</p> : <div className="mb-3" />}
          <div className="space-y-4">
            {group.fields.map((_f) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const f = _f as any;
              return (
              <div key={f.key} className="space-y-1">
                <label className="block text-sm font-bold text-oud-800">{f.label}</label>
                {f.type === 'select' ? (
                  <select
                    value={String(draft[f.key] ?? '')}
                    onChange={(e) => updateField(f.key, e.target.value)}
                    className="w-full bg-sand border border-oud-100 rounded-xl px-4 py-3 text-base text-oud-900 focus:outline-none focus:border-gold"
                  >
                    {(f as { options: Array<{ value: string; label: string }> }).options.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : f.type === 'slider' ? (
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-oud-600">٠٪</span>
                      <span className="text-2xl font-black text-oud-900 tabular-nums">{Number(draft[f.key] ?? 0)}٪</span>
                      <span className="text-sm text-oud-600">١٠٠٪</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={Number(draft[f.key] ?? 0)}
                      onChange={(e) => updateField(f.key, Number(e.target.value))}
                      className="w-full h-2 rounded-full appearance-none bg-oud-100 accent-gold cursor-pointer touch-none"
                    />
                  </div>
                ) : f.type === 'pillar' ? (
                  <InlinePillarField
                    text={String(draft[f.key] ?? '')}
                    onTextChange={(v) => updateField(f.key, v)}
                    iconName={String(draft[(f as { iconKey: string }).iconKey] ?? 'star')}
                    onIconChange={(v) => updateField((f as { iconKey: string }).iconKey, v)}
                    placeholder={COPY_MAP[f.key] ? t(COPY_MAP[f.key]!) : undefined}
                  />
                ) : f.type === 'theme' ? (
                  <div className="grid grid-cols-3 gap-3">
                    {(['classic', 'bold', 'minimal'] as const).map((theme) => (
                      <button
                        key={theme}
                        type="button"
                        onClick={() => updateField(f.key, theme)}
                        className={`rounded-2xl border p-4 text-center transition-all ${
                          draft[f.key] === theme
                            ? 'bg-oud-800 text-sand border-oud-800 shadow-quote'
                            : 'bg-cream text-oud-700 border-oud-100 hover:border-oud-200'
                        }`}
                      >
                        <div className={`mx-auto mb-2 w-12 h-8 rounded-lg ${
                          theme === 'classic' ? 'bg-oud-100' :
                          theme === 'bold' ? 'bg-gradient-to-b from-oud-800 to-oud-600' :
                          'bg-sand border border-oud-200'
                        }`} />
                        <span className="text-sm font-bold">
                          {theme === 'classic' ? 'كلاسيكي' : theme === 'bold' ? 'عصري' : 'بسيط'}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : f.type === 'color' ? (
                  <div className="flex items-center gap-3">
                    <label className="relative w-12 h-12 rounded-xl border border-oud-100 overflow-hidden cursor-pointer flex-none" style={{ backgroundColor: String(draft[f.key] ?? '#000000') }}>
                      <input
                        type="color"
                        value={String(draft[f.key] ?? '#000000')}
                        onChange={(e) => updateField(f.key, e.target.value)}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                      />
                    </label>
                    <input
                      type="text"
                      value={String(draft[f.key] ?? '')}
                      onChange={(e) => updateField(f.key, e.target.value)}
                      className="flex-1 bg-sand border border-oud-100 rounded-xl px-4 py-3 text-base text-oud-900 font-mono focus:outline-none focus:border-gold"
                      dir="ltr"
                    />
                  </div>
                ) : f.type === 'logo' ? (
                  <LogoUploader
                    currentUrl={String(draft[f.key] ?? '')}
                    onUploaded={(url) => updateField(f.key, url)}
                  />
                ) : f.type === 'toggle' ? (
                  <button
                    type="button"
                    onClick={() => updateField(f.key, !draft[f.key])}
                    className={`relative w-14 h-8 rounded-full transition-colors ${draft[f.key] ? 'bg-gold' : 'bg-oud-200'}`}
                  >
                    <span className={`absolute top-1 w-6 h-6 rounded-full bg-sand shadow transition-transform ${draft[f.key] ? 'right-1' : 'left-1'}`} />
                  </button>
                ) : f.type === 'blockedTypes' ? (
                  <div className="flex flex-wrap gap-2">
                    {BLOCKED_FURNITURE_TYPES.map((option) => {
                      const selected = Array.isArray(draft[f.key]) && (draft[f.key] as string[]).includes(option.value);
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            const current = Array.isArray(draft[f.key]) ? (draft[f.key] as string[]) : [];
                            const next = selected
                              ? current.filter((v) => v !== option.value)
                              : [...current, option.value];
                            updateField(f.key, next);
                          }}
                          className={`text-xs font-bold rounded-xl px-3 py-2 border transition-colors ${
                            selected
                              ? 'bg-oud-800 text-sand border-oud-800'
                              : 'bg-sand text-oud-600 border-oud-100 hover:border-oud-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <input
                    type={f.type}
                    value={String(draft[f.key] ?? '')}
                    onChange={(e) => updateField(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                    placeholder={COPY_MAP[f.key] ? t(COPY_MAP[f.key]!) : undefined}
                    className="w-full bg-sand border border-oud-100 rounded-xl px-4 py-3 text-base text-oud-900 placeholder-oud-300 focus:outline-none focus:border-gold"
                  />
                )}
                {f.hint ? <p className="text-xs text-oud-500">{f.hint}</p> : null}
              </div>
              ); })}

            {group.title === 'التسعير' ? (
              <div className="mt-4 bg-sand border border-oud-100 rounded-2xl p-4">
                <h3 className="text-sm font-bold text-oud-800 mb-2">مثال حي</h3>
                <p className="text-sm text-oud-700 leading-relaxed">
                  إذا كان السوق: أقل سعر <b>{exampleMin.toLocaleString()}</b> وأعلى سعر <b>{exampleMax.toLocaleString()}</b> (متوسط {exampleMid.toLocaleString()})
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <div className="bg-oud-800 text-sand rounded-2xl px-4 py-2 text-center">
                    <p className="text-xs text-gold-soft">سعرنا (شامل الضريبة)</p>
                    <p className="text-2xl font-black tabular-nums">{exampleDisplay.toLocaleString()} ر.س</p>
                  </div>
                  <div className="text-sm text-oud-600">
                    <p>قبل الضريبة: {examplePrice.toLocaleString()} ر.س</p>
                    <p>نسبة التوفير: {exampleMid > 0 ? Math.round((1 - exampleDisplay / exampleMid) * 100) : 0}٪ عن المتوسط</p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ))}

      {isDemo ? null : (
        <>
          <div className="h-20" />
          <div className={`fixed inset-x-0 bottom-0 z-20 backdrop-blur border-t transition-colors ${hasChanges ? 'bg-gold/10 border-gold/30' : 'bg-sand/95 border-oud-100'}`}>
            <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
              <button type="button" onClick={() => saveMut.mutate(draft)}
                disabled={saveMut.isPending}
                className="bg-oud-800 text-sand rounded-2xl px-6 py-3 text-base font-bold disabled:opacity-40 shadow-soft hover:bg-oud-900 hover:shadow-quote active:scale-[0.98] transition-all cursor-pointer">
                {saveMut.isPending ? 'جارٍ الحفظ…' : 'حفظ الإعدادات'}
              </button>
              {hasChanges && !saved ? (
                <span className="text-sm text-gold-deep font-bold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                  تغييرات غير محفوظة
                </span>
              ) : null}
              {saved ? <span className="text-sm text-green-700 font-bold">تم الحفظ ✓</span> : null}
              {saveMut.error ? <span className="text-sm text-red-700 font-bold">خطأ في الحفظ</span> : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Pager({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  return (
    <div className="flex items-center justify-center gap-3 pt-4">
      <button
        type="button"
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
        className="text-sm font-bold text-oud-700 hover:text-oud-900 disabled:opacity-30 disabled:cursor-not-allowed bg-cream border border-oud-100 rounded-xl px-4 py-2"
      >
        السابق
      </button>
      <span className="text-sm text-oud-600 tabular-nums">
        {page + 1} / {totalPages}
      </span>
      <button
        type="button"
        disabled={page >= totalPages - 1}
        onClick={() => onChange(page + 1)}
        className="text-sm font-bold text-oud-700 hover:text-oud-900 disabled:opacity-30 disabled:cursor-not-allowed bg-cream border border-oud-100 rounded-xl px-4 py-2"
      >
        التالي
      </button>
    </div>
  );
}

function LogoUploader({ currentUrl, onUploaded }: { currentUrl: string; onUploaded: (url: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('logo', file);
      const res = await fetch('/api/admin/upload-logo', {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) throw new Error('Upload failed');
      const body = (await res.json()) as { url: string };
      onUploaded(body.url);
    } catch {
      setError('فشل رفع الشعار');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      {currentUrl ? (
        <img src={currentUrl} alt="الشعار الحالي" className="h-12 w-auto object-contain bg-sand border border-oud-100 rounded-xl p-1" />
      ) : (
        <div className="h-12 w-12 rounded-xl bg-sand border border-oud-100 flex items-center justify-center text-oud-300 text-xs">—</div>
      )}
      <label className="cursor-pointer bg-sand border border-oud-100 rounded-xl px-4 py-2.5 text-sm font-bold text-oud-700 hover:border-gold transition-colors">
        {uploading ? 'جارٍ الرفع…' : 'رفع شعار'}
        <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" className="hidden" onChange={handleFile} disabled={uploading} />
      </label>
      {currentUrl ? (
        <button type="button" onClick={() => onUploaded('')}
          className="text-sm text-red-700 hover:text-red-900 font-bold">
          إزالة
        </button>
      ) : null}
      {error ? <span className="text-sm text-red-700">{error}</span> : null}
    </div>
  );
}

function normalizePhone(phone: string): string {
  const trimmed = phone.trim();
  if (trimmed.startsWith('+') || trimmed.startsWith('0')) return trimmed;
  if (trimmed.startsWith('5')) return `+966${trimmed}`;
  if (trimmed.startsWith('966')) return `+${trimmed}`;
  return `+${trimmed}`;
}

function InlinePillarField({ text, onTextChange, iconName, onIconChange, placeholder }: {
  text: string;
  onTextChange: (v: string) => void;
  iconName: string;
  onIconChange: (v: string) => void;
  placeholder?: string;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          className="flex-none w-12 h-12 rounded-xl bg-cream border border-oud-100 text-gold-deep flex items-center justify-center hover:border-gold transition-colors"
        >
          <PillarIcon name={iconName} />
        </button>
        <input
          type="text"
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-sand border border-oud-100 rounded-xl px-4 py-3 text-base text-oud-900 placeholder-oud-300 focus:outline-none focus:border-gold"
        />
      </div>
      {pickerOpen ? (
        <div className="absolute top-14 right-0 z-30 bg-cream border border-oud-100 rounded-2xl shadow-quote p-3 grid grid-cols-5 gap-2 w-64">
          {ICON_KEYS.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => { onIconChange(name); setPickerOpen(false); }}
              className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-colors ${
                iconName === name
                  ? 'bg-oud-800 text-gold border-oud-800'
                  : 'bg-sand text-oud-600 border-oud-100 hover:border-oud-200'
              }`}
            >
              <PillarIcon name={name} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PlanBanner({ tenant }: { tenant: { status: string; trialEndsAt: string | null; paidUntil: string | null } }) {
  const WHATSAPP = '966503875324';

  if (tenant.status === 'active' && tenant.paidUntil) {
    const daysLeft = Math.ceil((parseDate(tenant.paidUntil).getTime() - Date.now()) / 86400000);
    return (
      <div className="bg-green-50 border-b border-green-200 px-5 py-2 text-center text-sm">
        <span className="text-green-800 font-bold">اشتراك فعّال</span>
        <span className="text-green-700 mr-2">· ينتهي بعد {daysLeft} يوم</span>
      </div>
    );
  }

  if (tenant.status === 'suspended') {
    return (
      <div className="bg-red-50 border-b border-red-200 px-5 py-3 text-center space-y-1">
        <p className="text-sm text-red-800 font-bold">انتهت فترة التجربة</p>
        <a
          href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('مرحبًا، أرغب بتفعيل اشتراك سنديان')}`}
          target="_blank" rel="noreferrer noopener"
          className="inline-block text-xs bg-red-800 text-white rounded-full px-4 py-1.5 font-bold hover:bg-red-900 transition-colors"
        >
          تواصل لتفعيل الاشتراك
        </a>
      </div>
    );
  }

  if (tenant.status === 'trial' && tenant.trialEndsAt) {
    const endsAt = parseDate(tenant.trialEndsAt);
    const msLeft = endsAt.getTime() - Date.now();
    const hoursLeft = Math.max(0, Math.ceil(msLeft / 3600000));
    const daysLeft = Math.floor(hoursLeft / 24);
    const hrsRemainder = hoursLeft % 24;
    const timeStr = daysLeft > 0 ? `${daysLeft} يوم ${hrsRemainder > 0 ? `و ${hrsRemainder} ساعة` : ''}` : `${hoursLeft} ساعة`;

    return (
      <div className="bg-amber-50 border-b border-amber-200 px-5 py-2 text-center text-sm">
        <span className="text-amber-800 font-bold">تجربة مجانية</span>
        <span className="text-amber-700 mr-2">· متبقي {timeStr}</span>
        <a
          href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('مرحبًا، أرغب بتفعيل اشتراك سنديان')}`}
          target="_blank" rel="noreferrer noopener"
          className="text-amber-900 font-bold underline underline-offset-2 mr-2"
        >
          فعّل الاشتراك
        </a>
      </div>
    );
  }

  return null;
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return <p className="text-base text-oud-500 py-10 text-center">{children}</p>;
}
